import { FileText } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useActiveFeeBasisTypes,
  useActiveWaterfallTypes,
} from '@/hooks/react-query/useLPATypes';

interface LPATermsFormData {
  management_fee_investment_period: string;
  management_fee_post_investment: string;
  management_fee_basis_investment: string;
  management_fee_basis_post_investment: string;
  gp_commitment: string;
  waterfall_type: string;
  hurdle_rate: string;
  gp_catch_up: string;
  carried_interest: string;
  investment_period_years: string;
  fund_costs_cap: string;
  base_currency: string;
}

interface LPATermsFormProps {
  formData: LPATermsFormData;
  onInputChange: (field: keyof LPATermsFormData, value: string) => void;
  errors?: Record<string, string>;
}

export function LPATermsForm({
  formData,
  onInputChange,
  errors = {},
}: LPATermsFormProps) {
  const { data: feeBasisTypes = [], isLoading: feeBasisLoading } =
    useActiveFeeBasisTypes();
  const { data: waterfallTypes = [], isLoading: waterfallLoading } =
    useActiveWaterfallTypes();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-700" />
          <CardTitle>LPA Terms</CardTitle>
        </div>
        <CardDescription>
          Limited Partnership Agreement terms and conditions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Management Fees Section */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Management Fees</h4>
          <div className="space-y-4">
            {/* Investment Period Fees */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Investment Period
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="management_fee_investment_period">
                    Fee Rate (%)
                  </Label>
                  <Input
                    id="management_fee_investment_period"
                    type="number"
                    step="0.1"
                    value={formData.management_fee_investment_period}
                    onChange={(e) =>
                      onInputChange(
                        'management_fee_investment_period',
                        e.target.value
                      )
                    }
                    placeholder="2.0"
                    className={
                      errors.management_fee_investment_period
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  {errors.management_fee_investment_period && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.management_fee_investment_period}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="management_fee_basis_investment">
                    Fee Basis *
                  </Label>
                  <Select
                    value={formData.management_fee_basis_investment}
                    onValueChange={(value) =>
                      onInputChange('management_fee_basis_investment', value)
                    }
                    disabled={feeBasisLoading}
                  >
                    <SelectTrigger
                      id="management_fee_basis_investment"
                      className={
                        errors.management_fee_basis_investment
                          ? 'border-red-500'
                          : ''
                      }
                    >
                      <SelectValue
                        placeholder={
                          feeBasisLoading ? 'Loading...' : 'Select fee basis'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {feeBasisTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.management_fee_basis_investment && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.management_fee_basis_investment}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Post-Investment Period Fees */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Post-Investment Period
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="management_fee_post_investment">
                    Fee Rate (%)
                  </Label>
                  <Input
                    id="management_fee_post_investment"
                    type="number"
                    step="0.1"
                    value={formData.management_fee_post_investment}
                    onChange={(e) =>
                      onInputChange(
                        'management_fee_post_investment',
                        e.target.value
                      )
                    }
                    placeholder="1.5"
                    className={
                      errors.management_fee_post_investment
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  {errors.management_fee_post_investment && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.management_fee_post_investment}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="management_fee_basis_post_investment">
                    Fee Basis *
                  </Label>
                  <Select
                    value={formData.management_fee_basis_post_investment}
                    onValueChange={(value) =>
                      onInputChange(
                        'management_fee_basis_post_investment',
                        value
                      )
                    }
                    disabled={feeBasisLoading}
                  >
                    <SelectTrigger
                      id="management_fee_basis_post_investment"
                      className={
                        errors.management_fee_basis_post_investment
                          ? 'border-red-500'
                          : ''
                      }
                    >
                      <SelectValue
                        placeholder={
                          feeBasisLoading ? 'Loading...' : 'Select fee basis'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {feeBasisTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.management_fee_basis_post_investment && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.management_fee_basis_post_investment}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Period & Terms */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">
            Investment Period & Terms
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="investment_period_years">
                Investment Period (Years)
              </Label>
              <Input
                id="investment_period_years"
                type="number"
                value={formData.investment_period_years}
                onChange={(e) =>
                  onInputChange('investment_period_years', e.target.value)
                }
                placeholder="5"
                className={
                  errors.investment_period_years ? 'border-red-500' : ''
                }
              />
              {errors.investment_period_years && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.investment_period_years}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="gp_commitment">GP Commitment (%)</Label>
              <Input
                id="gp_commitment"
                type="number"
                step="0.1"
                value={formData.gp_commitment}
                onChange={(e) => onInputChange('gp_commitment', e.target.value)}
                placeholder="1.0"
                className={errors.gp_commitment ? 'border-red-500' : ''}
              />
              {errors.gp_commitment && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.gp_commitment}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Waterfall Structure */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">
            Waterfall Structure
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="waterfall_type">Waterfall Type</Label>
              <Select
                value={formData.waterfall_type}
                onValueChange={(value) =>
                  onInputChange('waterfall_type', value)
                }
                disabled={waterfallLoading}
              >
                <SelectTrigger id="waterfall_type">
                  <SelectValue
                    placeholder={
                      waterfallLoading ? 'Loading...' : 'Select waterfall type'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {waterfallTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="hurdle_rate">Hurdle Rate (%)</Label>
              <Input
                id="hurdle_rate"
                type="number"
                step="0.1"
                value={formData.hurdle_rate}
                onChange={(e) => onInputChange('hurdle_rate', e.target.value)}
                placeholder="8.0"
                className={errors.hurdle_rate ? 'border-red-500' : ''}
              />
              {errors.hurdle_rate && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.hurdle_rate}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="gp_catch_up">GP Catch-Up (%)</Label>
              <Input
                id="gp_catch_up"
                type="number"
                step="0.1"
                value={formData.gp_catch_up}
                onChange={(e) => onInputChange('gp_catch_up', e.target.value)}
                placeholder="100"
                className={errors.gp_catch_up ? 'border-red-500' : ''}
              />
              {errors.gp_catch_up && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.gp_catch_up}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="carried_interest">Carried Interest (%)</Label>
              <Input
                id="carried_interest"
                type="number"
                step="0.1"
                value={formData.carried_interest}
                onChange={(e) =>
                  onInputChange('carried_interest', e.target.value)
                }
                placeholder="20.0"
                className={errors.carried_interest ? 'border-red-500' : ''}
              />
              {errors.carried_interest && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.carried_interest}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Operational Costs */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Operational Costs</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fund_costs_cap">
                Costs Cap (% of Committed Capital)
              </Label>
              <Input
                id="fund_costs_cap"
                type="number"
                step="0.1"
                value={formData.fund_costs_cap}
                onChange={(e) =>
                  onInputChange('fund_costs_cap', e.target.value)
                }
                placeholder="0.5"
                className={errors.fund_costs_cap ? 'border-red-500' : ''}
              />
              {errors.fund_costs_cap && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.fund_costs_cap}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
